import { BehaviorSubject } from 'rxjs';
import { inject, Injectable, OutputEmitterRef } from '@angular/core';
import {
  VideoPlayerDataModel,
  VideoPlayerParams,
  VideoPlayerTranslations,
  VideoPlayerViewModel
} from './video-player.model';
import { VideoPlayerRepository } from './video-player.repository';
import { TRANSLATIONS_PROVIDER } from '../../../translations.token';

@Injectable()
export class VideoPlayerPresenter {
  public readonly translationsProvider = inject(TRANSLATIONS_PROVIDER);
  private readonly videoPlayerRepository = inject(VideoPlayerRepository);

  async load(
    vm: BehaviorSubject<VideoPlayerViewModel | null>,
    videoPlayerParams: VideoPlayerParams,
    updateEmitter: OutputEmitterRef<{ id: string; value: string | null }>
  ) {
    await this.videoPlayerRepository.load(videoPlayerParams, (dataModel: VideoPlayerDataModel) => {
      const videoPlayerViewModel: VideoPlayerViewModel = {
        canRecord: dataModel.canRecord,
        canDelete: dataModel.canDelete,
        showHint: videoPlayerParams.showHint ?? true,
        showVideo: dataModel.showVideo || !!dataModel.videoSource,
        recordingFailed: dataModel.recordingFailed,
        id: dataModel.id,
        videoSource: dataModel.videoSource,
        readOnly: videoPlayerParams.readOnly ?? false,
        translations: this.getTranslations()
      };
      vm.next(videoPlayerViewModel);
      this.emitUpdate(videoPlayerParams.id, updateEmitter, dataModel.videoSource ?? null);
    });
  }

  private emitUpdate(
    id: string | undefined,
    emitter: OutputEmitterRef<{ id: string | undefined; value: string | null }>,
    videoData: string | null
  ): void {
    const isRemove = videoData === null;
    const isBlob = videoData?.startsWith('blob:');
    if (!isBlob) {
      emitter.emit({
        id,
        value: isRemove ? null : videoData
      });
    }
  }

  private getTranslations(): VideoPlayerTranslations {
    return {
      maximumLengthHint: this.translationsProvider.getTranslation('video.maximumLengthHint'),
      recordingFailed: this.translationsProvider.getTranslation('video.recordingFailed')
    };
  }

  public async startRecording() {
    await this.videoPlayerRepository.startRecording();
  }

  public stopRecording() {
    this.videoPlayerRepository.stopRecording();
  }

  public pauseRecording() {
    this.videoPlayerRepository.pauseRecording();
  }

  public resumeRecording() {
    this.videoPlayerRepository.resumeRecording();
  }

  public async removeRecording() {
    await this.videoPlayerRepository.removeRecording();
  }
}
